import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { isInsideGrid, moveCursor, tapCell } from "../game/commands";
import { getEngine } from "../game/runtime";
import { useGameUi } from "../game/store";
import { t } from "../i18n";
import type { Camera } from "./camera";
import { StationScene } from "./scene";

/** Au-delà de ce déplacement (pixels CSS), un appui devient un glissé et ne choisit plus de case. */
const TAP_SLOP = 8;

interface Pointer {
  x: number;
  y: number;
  startX: number;
  startY: number;
}

let activeScene: StationScene | null = null;

/** La scène affichée, pour les commandes de la caméra (recentrer) et les marges de l'interface. */
export function currentScene(): StationScene | null {
  return activeScene;
}

/**
 * Le plan de la station : la scène Phaser, plus les gestes (toucher, glisser, pincer, molette) et le clavier
 * (flèches, Entrée, Échap, + et −).
 */
export function StationCanvas({ insets }: { insets: Camera["insets"] }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<StationScene | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const scene = new StationScene();
    sceneRef.current = scene;
    activeScene = scene;
    scene.mount(host);
    // En développement, la scène s'inspecte depuis la console.
    if (import.meta.env.DEV) Object.assign(window, { __stationScene: scene });
    return () => {
      scene.destroy();
      if (activeScene === scene) activeScene = null;
      sceneRef.current = null;
    };
  }, []);

  useEffect(() => {
    sceneRef.current?.setInsets(insets);
  }, [insets]);

  // Le curseur clavier ne s'affiche que si le focus vient du clavier, pas d'un clic.
  const lastPointerDown = useRef(0);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const pointers = new Map<number, Pointer>();
    let moved = false;
    let pinch: { distance: number; scale: number } | null = null;

    const local = (event: PointerEvent | WheelEvent) => {
      const rect = host.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const onDown = (event: PointerEvent) => {
      lastPointerDown.current = performance.now();
      try {
        host.setPointerCapture(event.pointerId);
      } catch {
        // Pointeur déjà relâché (ou synthétique) : le geste continue sans capture.
      }
      const point = local(event);
      pointers.set(event.pointerId, { ...point, startX: point.x, startY: point.y });
      if (pointers.size === 1) moved = false;
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()] as [Pointer, Pointer];
        pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y), scale: sceneRef.current?.camera.scale ?? 1 };
        moved = true;
      }
    };

    const onMove = (event: PointerEvent) => {
      const scene = sceneRef.current;
      const point = local(event);
      if (event.pointerType === "mouse" && pointers.size === 0 && scene) {
        const ui = useGameUi.getState();
        const target = scene.pick(point.x, point.y, Boolean(ui.buildType));
        const cell = target?.kind === "cell" && isInsideGrid(target.cell) ? target.cell : null;
        ui.setHover(cell);
        // La main sur ce qui se touche : un vaisseau, un module.
        const modules = getEngine()?.state.modules ?? [];
        const touchable = target?.kind === "ship" || (cell !== null && !ui.buildType && modules.some((module) => module.x === cell.x && module.y === cell.y));
        host.style.cursor = touchable ? "pointer" : "";
      }
      const pointer = pointers.get(event.pointerId);
      if (!pointer || !scene) return;
      const dx = point.x - pointer.x;
      const dy = point.y - pointer.y;
      pointer.x = point.x;
      pointer.y = point.y;
      if (pointers.size === 1) {
        if (Math.hypot(point.x - pointer.startX, point.y - pointer.startY) > TAP_SLOP) moved = true;
        if (moved) scene.pan(dx, dy);
      } else if (pointers.size === 2 && pinch) {
        const [a, b] = [...pointers.values()] as [Pointer, Pointer];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        const midX = (a.x + b.x) / 2;
        const midY = (a.y + b.y) / 2;
        const target = pinch.scale * (distance / Math.max(1, pinch.distance));
        scene.zoomAt(target / scene.camera.scale, midX, midY);
        scene.pan(dx / 2, dy / 2);
      }
    };

    const onUp = (event: PointerEvent) => {
      const pointer = pointers.get(event.pointerId);
      pointers.delete(event.pointerId);
      if (pointers.size < 2) pinch = null;
      const scene = sceneRef.current;
      if (!pointer || !scene || moved || pointers.size > 0 || event.type === "pointercancel") return;
      const ui = useGameUi.getState();
      // En construction, on vise toujours la case du sol, même derrière un bâtiment.
      const target = scene.pick(pointer.x, pointer.y, Boolean(ui.buildType));
      if (target?.kind === "ship") ui.selectShip(target.id);
      else if (target && isInsideGrid(target.cell)) tapCell(target.cell);
      else if (!ui.buildType) ui.closeAll();
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const scene = sceneRef.current;
      if (!scene) return;
      const point = local(event);
      scene.zoomAt(Math.exp(-event.deltaY / 500), point.x, point.y);
    };

    const onLeave = () => {
      useGameUi.getState().setHover(null);
      host.style.cursor = "";
    };

    host.addEventListener("pointerdown", onDown);
    host.addEventListener("pointermove", onMove);
    host.addEventListener("pointerup", onUp);
    host.addEventListener("pointercancel", onUp);
    host.addEventListener("pointerleave", onLeave);
    host.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      host.removeEventListener("pointerdown", onDown);
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerup", onUp);
      host.removeEventListener("pointercancel", onUp);
      host.removeEventListener("pointerleave", onLeave);
      host.removeEventListener("wheel", onWheel);
    };
  }, []);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const scene = sceneRef.current;
    const ui = useGameUi.getState();
    const moves: Record<string, [number, number]> = {
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      ui.setCanvasFocused(true);
      const cell = moveCursor(move[0], move[1]);
      scene?.ensureCellVisible(cell);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      tapCell(ui.cursor);
    } else if (event.key === "+" || event.key === "=") {
      scene?.zoomAt(1.2);
    } else if (event.key === "-") {
      scene?.zoomAt(1 / 1.2);
    } else if (event.key === "0") {
      scene?.recenter();
    }
  };

  return (
    <div
      ref={hostRef}
      role="application"
      aria-label={t("app.canvasLabel")}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onFocus={() => useGameUi.getState().setCanvasFocused(performance.now() - lastPointerDown.current > 300)}
      onBlur={() => useGameUi.getState().setCanvasFocused(false)}
      className="station-canvas absolute inset-0 outline-none"
    />
  );
}
